import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Award, Check, Loader2, Lock, Send } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const API_URL = "";
const PLACEMENTS = ['1st', '2nd', '3rd'];
const PLACEMENT_COLORS = {
  '1st': 'bg-yellow-500 text-white',
  '2nd': 'bg-stone-400 text-white',
  '3rd': 'bg-amber-700 text-white'
};

export const JudgePortal = ({ previewMode = false, authToken = '' }) => {
  const { exhibitionId } = useParams();
  const [searchParams] = useSearchParams();
  const judgeToken = searchParams.get('token') || '';

  // Build the correct base path and headers based on mode
  const apiBase = previewMode
    ? `${API_URL}/api/judging/preview/${exhibitionId}`
    : `${API_URL}/api/judging/portal/${exhibitionId}`;
  const authHeaders = previewMode
    ? { 'Authorization': `Bearer ${authToken}` }
    : {};
  const tokenQS = previewMode ? '' : `?token=${encodeURIComponent(judgeToken)}`;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}${tokenQS}`, { headers: authHeaders });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Access denied');
      }
      const d = await res.json();
      setData(d);
      // Set initial tab to first category on first load only
      setActiveTab(prev => prev ?? (d.categories[0]?.id ?? '_uncategorized'));
      const hasSubmitted = d.awards?.some(a => a.status === 'submitted');
      if (hasSubmitted) setSubmitted(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [apiBase, tokenQS]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setPlacement = async (categoryId, placement, artworkId) => {
    try {
      const res = await fetch(`${apiBase}/set-placement${tokenQS}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ medium_category_id: categoryId, placement, submission_artwork_id: artworkId })
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success(`${placement} Place set`);
      fetchData();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const removePlacement = async (awardId) => {
    try {
      await fetch(`${apiBase}/remove-placement${tokenQS}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ award_id: awardId })
      });
      fetchData();
    } catch (e) {
      toast.error('Failed to remove placement');
    }
  };

  const submitJudging = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${apiBase}/submit-judging${tokenQS}`, {
        method: 'POST', headers: authHeaders
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      setSubmitted(true);
      setConfirmed(true);
      fetchData();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Disable right-click on images
  const preventContextMenu = (e) => e.preventDefault();

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-stone-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center" data-testid="judge-error">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="py-12 text-center">
            <Lock size={48} className="mx-auto mb-4 text-red-400" />
            <h1 className="text-2xl font-serif mb-2">Access Denied</h1>
            <p className="text-stone-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { exhibition, categories, artworks, awards } = data;

  // Group artworks by medium category
  const grouped = {};
  for (const cat of categories) {
    grouped[cat.id] = {
      category: cat,
      artworks: artworks.filter(a => a.medium_category_id === cat.id),
      awards: awards.filter(a => a.medium_category_id === cat.id)
    };
  }
  // Add uncategorized
  const categorizedIds = new Set(categories.map(c => c.id));
  const uncategorized = artworks.filter(a => !a.medium_category_id || !categorizedIds.has(a.medium_category_id));
  if (uncategorized.length > 0) {
    grouped['_uncategorized'] = {
      category: { id: '_uncategorized', name: 'Uncategorized' },
      artworks: uncategorized,
      awards: awards.filter(a => !categorizedIds.has(a.medium_category_id))
    };
  }

  const getAwardForPlacement = (categoryId, placement) =>
    awards.find(a => a.medium_category_id === categoryId && a.placement === placement);

  const getAwardForArtwork = (categoryId, artworkId) =>
    awards.find(a => a.medium_category_id === categoryId && a.submission_artwork_id === artworkId);

  const totalCategories = Object.keys(grouped).length;
  const completeCategories = Object.entries(grouped).filter(([, g]) =>
    PLACEMENTS.every(p => g.awards.some(a => a.placement === p))
  ).length;

  return (
    <div className="min-h-screen bg-stone-100" data-testid="judge-portal">
      <SEOHead title={`Judging — ${exhibition?.title || ''}`} />

      {/* Preview mode banner */}
      {previewMode && (
        <div className="bg-amber-500 text-white text-center py-3 px-4 font-semibold text-sm tracking-wide" data-testid="preview-banner">
          JUDGE PREVIEW MODE — Not Visible to Guest Judge
        </div>
      )}

      {/* Header */}
      <div className="bg-stone-900 text-white py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <Award size={28} className="text-yellow-400" />
            <span className="text-sm uppercase tracking-widest text-stone-400">Blind Judging Portal</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-serif">{exhibition?.title}</h1>
          <p className="text-stone-300 mt-2 text-base">Select 1st, 2nd, and 3rd place for each category.</p>
          <p className="text-stone-400 text-sm mt-1">{artworks.length} artwork{artworks.length !== 1 ? 's' : ''} to review &mdash; {completeCategories}/{totalCategories} categories complete</p>
        </div>
      </div>

      {/* Confirmation banner */}
      {confirmed && (
        <div className="bg-green-700 text-white text-center py-4 px-4 text-base font-medium" data-testid="save-confirmed-banner">
          Your selections have been saved. You may return using this link until judging closes.
        </div>
      )}

      {/* Step instructions */}
      <div className="bg-stone-800 text-stone-200 px-4 py-4" data-testid="judge-step-instructions">
        <div className="max-w-6xl mx-auto flex flex-wrap gap-6 text-sm">
          <span><strong className="text-white">Step 1:</strong> Choose a category tab below.</span>
          <span><strong className="text-white">Step 2:</strong> Select 1st, 2nd, and 3rd place for each artwork.</span>
          <span><strong className="text-white">Step 3:</strong> Click <em>Submit Selections</em> when all categories are complete.</span>
        </div>
      </div>

      {/* Info banner */}
      <div className="bg-amber-50 border-b border-amber-200 px-4 py-3" data-testid="judge-info-banner">
        <p className="max-w-6xl mx-auto text-sm text-amber-800">
          Choose exactly one 1st, one 2nd, and one 3rd in each category. You can change your choices at any time before submitting.
        </p>
      </div>

      {/* Category Tabs */}
      <div className="bg-white border-b border-stone-200 px-4 sticky top-0 z-10 shadow-sm">
        <div className="max-w-6xl mx-auto">
          <div className="flex gap-1 overflow-x-auto py-2" role="tablist">
            {Object.entries(grouped).map(([catId, group]) => {
              const isComplete = PLACEMENTS.every(p => group.awards.some(a => a.placement === p));
              return (
                <button
                  key={catId}
                  role="tab"
                  aria-selected={activeTab === catId}
                  onClick={() => setActiveTab(catId)}
                  className={`whitespace-nowrap px-5 py-2.5 rounded-lg text-base font-medium transition-colors flex-shrink-0 ${
                    activeTab === catId
                      ? 'bg-stone-900 text-white'
                      : 'text-stone-600 hover:bg-stone-100'
                  }`}
                  data-testid={`tab-${catId}`}
                >
                  {group.category.name}
                  {isComplete && <span className="ml-2 text-green-400 font-bold">✓</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Active Category Content */}
      <div className="max-w-6xl mx-auto px-4 py-8 pb-32">
        {Object.entries(grouped).filter(([catId]) => catId === activeTab).map(([catId, group]) => {
          const catAwards = group.awards;
          const isComplete = PLACEMENTS.every(p => catAwards.some(a => a.placement === p));

          return (
            <section key={catId} data-testid={`category-section-${catId}`}>
              <div className="flex items-center gap-3 mb-5">
                <h2 className="text-2xl sm:text-3xl font-serif text-stone-900">{group.category.name}</h2>
                {isComplete && <Badge className="bg-green-100 text-green-800 text-sm px-3 py-1"><Check size={14} className="mr-1" /> Complete</Badge>}
              </div>

              {/* Current placements summary */}
              <div className="flex flex-wrap gap-3 mb-7">
                {PLACEMENTS.map(p => {
                  const award = getAwardForPlacement(catId, p);
                  const aw = award ? artworks.find(a => a.id === award.submission_artwork_id) : null;
                  return (
                    <div key={p} className={`rounded-lg px-4 py-2 text-sm ${award ? PLACEMENT_COLORS[p] : 'bg-stone-200 text-stone-500'}`} data-testid={`placement-badge-${catId}-${p}`}>
                      <span className="font-bold">{p}:</span> {aw ? aw.title : 'Not yet selected'}
                      {award && (award.status === 'draft' || award.status === 'submitted') && (
                        <button onClick={() => removePlacement(award.id)} className="ml-2 opacity-70 hover:opacity-100 text-lg leading-none">&times;</button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Artwork grid */}
              {group.artworks.length === 0 ? (
                <p className="text-stone-400 italic text-lg">No artworks in this category.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {group.artworks.map(aw => {
                    const existingAward = getAwardForArtwork(catId, aw.id);
                    return (
                      <Card key={aw.id} className={`overflow-hidden transition-shadow ${existingAward ? 'ring-2 ring-yellow-400 shadow-lg' : 'hover:shadow-md'}`} data-testid={`judge-artwork-${aw.id}`}>
                        {aw.image_urls?.[0] && (
                          <div className="aspect-square bg-stone-200 relative select-none"
                            onContextMenu={preventContextMenu} draggable={false}>
                            <img
                              src={aw.image_urls[0]}
                              alt={aw.title}
                              className="w-full h-full object-cover pointer-events-none select-none"
                              draggable={false}
                              onContextMenu={preventContextMenu}
                              style={{ WebkitUserDrag: 'none' }}
                            />
                            <div className="absolute inset-0" onContextMenu={preventContextMenu} />
                            {aw.image_urls.length > 1 && (
                              <Badge className="absolute bottom-2 right-2 bg-black/60 text-white text-xs">+{aw.image_urls.length - 1} more</Badge>
                            )}
                            {existingAward && (
                              <Badge className={`absolute top-2 left-2 ${PLACEMENT_COLORS[existingAward.placement]} text-base px-3 py-1`}>
                                {existingAward.placement} Place
                              </Badge>
                            )}
                          </div>
                        )}
                        <CardContent className="p-5">
                          <h3 className="font-semibold text-stone-900 text-xl mb-1">{aw.title}</h3>
                          <p className="text-sm text-stone-500">{aw.size} &bull; {aw.medium}</p>

                          {/* Placement buttons — elderly-friendly large tap targets */}
                          <div className="flex flex-col gap-2 mt-4">
                            {PLACEMENTS.map(p => {
                              const isThisPlacement = existingAward?.placement === p;
                              const otherHasPlacement = catAwards.some(
                                a => a.placement === p && a.submission_artwork_id !== aw.id
                              );
                              return (
                                <Button
                                  key={p}
                                  size="default"
                                  variant={isThisPlacement ? 'default' : 'outline'}
                                  className={`w-full text-base py-5 h-auto ${isThisPlacement ? PLACEMENT_COLORS[p] : otherHasPlacement ? 'opacity-40 cursor-not-allowed' : ''}`}
                                  onClick={() => !otherHasPlacement && setPlacement(catId, p, aw.id)}
                                  disabled={otherHasPlacement && !isThisPlacement}
                                  data-testid={`set-${p}-${aw.id}`}
                                >
                                  {isThisPlacement ? <><Check size={16} className="mr-1.5" /> {p} Place Selected</> : `Select ${p} Place`}
                                </Button>
                              );
                            })}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}

        {/* Empty state when no tab is active */}
        {!activeTab && (
          <p className="text-stone-400 italic text-lg text-center py-20">No categories to judge.</p>
        )}
      </div>

      {/* Sticky bottom bar with progress + Save button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-stone-200 py-3 px-4 shadow-2xl z-20" data-testid="judge-bottom-bar">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className="flex gap-4 flex-wrap text-sm font-medium">
            {Object.entries(grouped).map(([catId, group]) => {
              const isComplete = PLACEMENTS.every(p => group.awards.some(a => a.placement === p));
              return (
                <span key={catId} className={isComplete ? 'text-green-600' : 'text-stone-400'}>
                  {group.category.name}: {isComplete ? '✓' : `${group.awards.length}/3`}
                </span>
              );
            })}
          </div>
          <Button
            size="lg"
            className="bg-stone-900 hover:bg-stone-800 text-white text-base px-8 py-5 h-auto"
            onClick={submitJudging}
            disabled={submitting || (completeCategories < totalCategories)}
            data-testid="submit-judging-btn"
          >
            {submitting ? <><Loader2 size={18} className="mr-2 animate-spin" /> Submitting...</>
              : submitted ? <><Check size={18} className="mr-2" /> Selections Submitted</>
              : <><Send size={18} className="mr-2" /> Submit Selections</>}
          </Button>
        </div>
        {!submitted && completeCategories < totalCategories && (
          <p className="text-stone-400 text-xs text-center mt-1">
            Complete all categories ({completeCategories}/{totalCategories}) to submit
          </p>
        )}
      </div>

      {/* Global CSS to prevent image saving */}
      <style>{`
        [data-testid="judge-portal"] img { -webkit-user-select: none; user-select: none; pointer-events: none; }
        [data-testid="judge-portal"] .aspect-square { -webkit-touch-callout: none; }
      `}</style>
    </div>
  );
};

export default JudgePortal;
